import { useState } from "react";
import { ArrowRight, Eye, EyeOff, Lock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

function PasswordInput({ label, value, onChange, autoComplete }) {
  const [visible, setVisible] = useState(false);

  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
        {label} <span className="text-blue-500">*</span>
      </label>
      <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-3 transition focus-within:border-blue-400 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-500/10">
        <Lock size={17} className="text-slate-400" />
        <input
          type={visible ? "text" : "password"}
          required
          maxLength={30}
          value={value}
          onChange={(e) => onChange(e.target.value.slice(0, 30))}
          autoComplete={autoComplete}
          className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
        />
        <button
          type="button"
          onClick={() => setVisible((next) => !next)}
          aria-label={visible ? "Hide password" : "Show password"}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          {visible ? <EyeOff size={17} /> : <Eye size={17} />}
        </button>
      </div>
    </div>
  );
}

export function ForcePasswordChange() {
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setLoading(true);
    const res = await changePassword(currentPassword, newPassword);
    setLoading(false);
    if (!res.ok) {
      toast.error(res.error || "Could not update password");
      return;
    }
    toast.success("Password updated");
  }

  return (
    <div className="hx-auth-shell min-h-screen flex items-center justify-center p-4 sm:p-6">
      <form
        onSubmit={submit}
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/60 sm:p-8"
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-red-600 to-red-400" />
        <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[11px] font-semibold text-emerald-700">
          <ShieldCheck size={13} />
          First login security
        </div>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">Change your password</h1>
        <p className="mt-2 text-sm leading-6 text-slate-500">
          Enter the current password given by the platform admin, then set a new password before continuing.
        </p>
        <div className="mt-8 space-y-5">
          <PasswordInput label="Current Password" value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" />
          <PasswordInput label="New Password" value={newPassword} onChange={setNewPassword} autoComplete="new-password" />
          <PasswordInput label="Confirm New Password" value={confirmPassword} onChange={setConfirmPassword} autoComplete="new-password" />
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-3.5 text-sm font-bold text-white shadow-lg shadow-red-600/20 transition hover:bg-blue-700 hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60 disabled:translate-y-0"
          >
            {loading ? "Updating..." : <>Update password <ArrowRight size={16} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}
